<template>
  <span v-if="guides.length" class="video-guide">
    <v-dialog v-model="open" max-width="960" content-class="video-guide-dialog" :aria-label="selected ? selected.title : 'Video guides'">
      <template v-slot:activator="{ on, attrs }">
        <button type="button" class="video-guide-trigger" v-bind="attrs" v-on="on" @click.stop>
          <i class="mdi mdi-play-circle-outline" aria-hidden="true"></i>
          <span>{{ label || (guides.length > 1 ? 'Watch guides' : 'Watch guide') }}</span>
          <span v-if="guides.length === 1 && guides[0].duration" class="video-guide-duration">{{ guides[0].duration }}</span>
        </button>
      </template>
      <v-card class="video-guide-card">
        <div class="video-guide-header">
          <h2>{{ selected ? selected.title : 'Video guides' }}</h2>
          <v-btn icon aria-label="Close video guide" @click="open = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <template v-if="open && selected">
          <div class="video-guide-frame">
            <iframe :key="selected.id" :src="selected.embed" :title="selected.title" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
          </div>
          <div class="video-guide-footer">
            <p>{{ selected.description }}</p>
            <button v-if="guides.length > 1" type="button" class="video-guide-back" @click="selected = null">Back to guides</button>
            <a :href="watchUrl(selected)" target="_blank" rel="noopener noreferrer">Open in {{ selected.youtubeId || selected.embed.includes('youtube') ? 'YouTube' : 'Loom' }} <span class="sr-only">(new tab)</span><i class="mdi mdi-open-in-new ml-1" aria-hidden="true"></i></a>
          </div>
        </template>
        <div v-else-if="open" class="video-guide-list">
          <button v-for="guide in guides" :key="guide.id" type="button" @click="selected = guide">
            <i class="mdi mdi-play-circle-outline" aria-hidden="true"></i>
            <span><strong>{{ guide.title }}</strong><small>{{ guide.description }}</small></span>
            <span class="video-guide-duration">{{ guide.duration }}</span>
          </button>
        </div>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import { videoGuides, legacyGuides } from '@/data/videoGuides';

export default {
  name: 'VideoGuide',
  props: {
    ids: { type: String, required: true },
    label: { type: String, default: '' }
  },
  data: () => ({ open: false, selected: null }),
  computed: {
    guides() {
      const catalog = videoGuides.concat(legacyGuides);
      return this.ids.split(',').map(id => catalog.find(guide => guide.id === id.trim())).filter(Boolean);
    }
  },
  watch: {
    open(value) { this.selected = value && this.guides.length === 1 ? this.guides[0] : null; },
    '$route.fullPath'() { this.open = false; }
  },
  methods: {
    watchUrl(guide) {
      if (guide.youtubeId) return 'https://www.youtube.com/watch?v=' + guide.youtubeId;
      if (guide.embed.includes('youtube.com/embed/')) return guide.embed.replace('/embed/', '/watch?v=').split('?si=')[0];
      return guide.embed.replace('www.loom.com/embed/', 'www.loom.com/share/');
    }
  }
};
</script>

<style scoped>
.video-guide { display: inline-block; max-width: 100%; }
.video-guide-trigger { display: inline-flex; align-items: center; gap: 6px; min-height: 32px; padding: 4px 8px; border: 1px solid #e5e7eb; border-radius: 4px; background: #fff; color: #44468f; font-size: 12px; font-weight: 500; line-height: 1.4; text-align: left; }
.video-guide-trigger:hover { background: #f5f5fa; border-color: #c7c8df; }
.video-guide-trigger:focus-visible, .video-guide-list button:focus-visible, .video-guide-back:focus-visible { outline: 2px solid #44468f; outline-offset: 2px; }
.video-guide-trigger i { font-size: 17px; }
.video-guide-duration { color: #6b7280; font-size: 11px; white-space: nowrap; }
.video-guide-header { background: #fff; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 16px; }
.video-guide-header h2 { margin: 0; color: #1f2937; font-size: 16px; font-weight: 600; line-height: 1.4; }
.video-guide-frame { position: relative; width: 100%; padding-top: 56.25%; background: #111827; }
.video-guide-frame iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
.video-guide-footer { padding: 16px; font-size: 12px; color: #6b7280; }
.video-guide-footer p { margin-bottom: 12px; }
.video-guide-footer a, .video-guide-back { color: #44468f; }
.video-guide-back { margin-right: 20px; }
.video-guide-list { padding: 0 16px 16px; }
.video-guide-list button { display: flex; width: 100%; align-items: center; gap: 12px; padding: 16px 8px; border-top: 1px solid #e5e7eb; text-align: left; }
.video-guide-list button:hover { background: #f9fafb; }
.video-guide-list i { color: #44468f; font-size: 24px; }
.video-guide-list strong, .video-guide-list small { display: block; }
.video-guide-list strong { font-size: 13px; color: #1f2937; }
.video-guide-list small { margin-top: 4px; font-size: 12px; color: #6b7280; }
.video-guide-list .video-guide-duration { margin-left: auto; }
</style>
